export interface FlagshipProject {
  id: string;
  title: string;
  titleAr: string;
  brand: 'Debug / Wyzon' | '5vix' | 'Texio';
  category: string;
  categoryAr: string;
  description: string;
  descriptionAr: string;
  image: string;
  badge: string;
  badgeAr: string;
  webUrl?: string;
  iosUrl?: string;
  androidUrl?: string;
  features: { en: string; ar: string }[];
  keywords: string[];
  relatedBlogCategories: string[];
}

export const flagshipProjects: FlagshipProject[] = [
  {
    id: 'whatsapp-ai',
    title: 'WhatsApp AI Agent',
    titleAr: 'وكيل ذكاء اصطناعي للواتساب',
    brand: 'Debug / Wyzon',
    category: 'Enterprise AI & Automation',
    categoryAr: 'حلول ذكاء اصطناعي وأتمتة',
    description: 'Smart automated responses, bulk messaging, deep analytics, and 24/7 AI-powered customer support for WhatsApp.',
    descriptionAr: 'ردود ذكية ورسائل جماعية وتحليلات عميقة — منصة واحدة تدير كل محادثاتك باحترافية على مدار الساعة.',
    image: '/images/Enterprise-3.webp',
    badge: 'AI Automation',
    badgeAr: 'أتمتة ذكية',
    webUrl: 'https://wyzon.debugin.com/',
    features: [
      { en: '24/7 Intelligent Automated Support', ar: 'دعم ذكي مؤتمت على مدار 24/7' },
      { en: 'Bulk Messaging & CRM Sync', ar: 'رسائل جماعية ومزامنة مع أنظمة العملاء' },
      { en: 'Deep Conversation Analytics', ar: 'تحليلات تفصيلية لأداء المحادثات' },
      { en: 'Multi-Agent Team Inbox', ar: 'صندوق وارد موحد لفريق خدمة العملاء' },
    ],
    keywords: ['whatsapp ai bot', 'wyzon ai', 'whatsapp automation egypt', 'ai customer service'],
    relatedBlogCategories: ['Automation', 'Development'],
  },
  {
    id: '5vix-gym',
    title: '5vix Gym System',
    titleAr: 'نظام 5vix لإدارة الجيم',
    brand: '5vix',
    category: 'Fitness & Gym Management',
    categoryAr: 'إدارة الصالات الرياضية',
    description: 'All-in-one cloud platform for member registrations, subscriptions, gate access, trainer schedules, and financials.',
    descriptionAr: 'منصة سحابية متكاملة لإدارة الأعضاء والاشتراكات وبوابات الدخول وجداول المدربين والتقارير المالية.',
    image: '/images/Enterprise-1.webp',
    badge: 'Gym Management ERP',
    badgeAr: 'نظام إدارة الجيم',
    webUrl: 'https://5vix.com/',
    androidUrl: 'https://play.google.com/store/apps/details?id=com.fivevix',
    iosUrl: 'https://apps.apple.com/eg/app/5vix/id6553996855',
    features: [
      { en: 'Subscription & Membership Tracking', ar: 'تتبع الاشتراكات والعضويات والتجديد' },
      { en: 'Biometric & Gate Access Control', ar: 'ربط بوابات الدخول وأجهزة البصمة' },
      { en: 'PT Sessions & Schedule Manager', ar: 'إدارة جلسات التدريب الخاص والمواعيد' },
      { en: 'Comprehensive Financial Reports', ar: 'تقارير مالية وجرد مبيعات مفصل' },
    ],
    keywords: ['5vix gym', 'gym management system egypt', 'fitness club software'],
    relatedBlogCategories: ['Mobile', 'Development', 'Cloud'],
  },
  {
    id: '5vix-fit',
    title: '5vix Fit',
    titleAr: 'تطبيق 5vix Fit للأعضاء',
    brand: '5vix',
    category: 'Fitness Member App',
    categoryAr: 'تطبيق المتدربين والأعضاء',
    description: 'The official member app for 5vix gyms to check subscriptions, track workout logs, monitor attendance, and receive alerts.',
    descriptionAr: 'التطبيق الرسمي لأعضاء جيم فايفيكس، يتيح متابعة الاشتراكات والحضور وجداول التمارين والعروض بسهولة.',
    image: '/images/MOBILE-4.webp',
    badge: 'Mobile App',
    badgeAr: 'تطبيق الهاتف',
    androidUrl: 'https://play.google.com/store/apps/details?id=com.fivevix.fit',
    iosUrl: 'https://apps.apple.com/eg/app/5vix-fit/id6751123751',
    features: [
      { en: 'Instant QR Entry & Attendance', ar: 'دخول سريع وتأكيد حضور عبر كود QR' },
      { en: 'Live Subscription & Expiry Status', ar: 'متابعة صلاحية الاشتراك وتاريخ الانتهاء' },
      { en: 'Workout & Nutrition Logs', ar: 'جداول التمارين والمتابعة مع المدرب' },
      { en: 'Gym Notifications & Offers', ar: 'إشعارات فورية بعروض وأخبار الصالة' },
    ],
    keywords: ['5vix fit', 'gym member mobile app', 'fitness tracker egypt'],
    relatedBlogCategories: ['Mobile'],
  },
  {
    id: 'ai-medical',
    title: 'AI-Medical Analysis',
    titleAr: 'تحليل طبي بالذكاء الاصطناعي',
    brand: '5vix',
    category: 'Healthcare & Medical AI',
    categoryAr: 'الذكاء الاصطناعي الطبي',
    description: 'Intelligent medical imaging platform for automated detection of bone fractures, osteoarthritis, lung diseases, and scoliosis.',
    descriptionAr: 'نظام ذكي للتصوير الطبي للكشف الآلي عن الكسور، وخشونة المفاصل، واضطرابات الرئة، والجنف، وأمراض الأسنان بدقة فائقة.',
    image: '/images/Enterprise-4.webp',
    badge: 'Medical AI',
    badgeAr: 'ذكاء اصطناعي طبي',
    webUrl: 'https://ai.5vix.com/',
    features: [
      { en: 'AI Fracture & Trauma Detection', ar: 'الكشف الآلي الفوري عن الكسور والإصابات' },
      { en: 'Chest X-Ray & Lung Screening', ar: 'فحص أشعة الصدر واضطرابات الجهاز التنفسي' },
      { en: 'Orthopedic & Spine Analysis', ar: 'قياس درجات الجنف وتشوهات العمود الفقري' },
      { en: 'DICOM Integration & Fast Reports', ar: 'تكامل كامل مع صيغ الأشعة DICOM وتقارير فورية' },
    ],
    keywords: ['medical ai', 'ai diagnostic radiology', '5vix ai medical', 'fracture detection ai'],
    relatedBlogCategories: ['Performance', 'Cloud', 'Development'],
  },
  {
    id: 'textile-erp',
    title: 'Textile ERP System',
    titleAr: 'نظام (ERP) لمصانع المنسوجات',
    brand: 'Texio',
    category: 'Industrial Manufacturing ERP',
    categoryAr: 'نظام ERP لمصانع النسيج',
    description: 'Integrated ERP platform purpose-built for textile and garment factories to manage yarn spinning, weaving, dyeing, stock, and orders.',
    descriptionAr: 'نظام متكامل مصمم خصيصًا لصناعة النسيج والملابس لإدارة خطوط الإنتاج، المخزون، المبيعات، ومراحل التصنيع في منصة موحدة.',
    image: '/images/Enterprise-2.webp',
    badge: 'Textile ERP',
    badgeAr: 'نظام مصانع النسيج',
    webUrl: 'https://texio.debugin.com/',
    androidUrl: 'https://play.google.com/store/apps/details?id=com.texio',
    iosUrl: 'https://apps.apple.com/eg/app/texio/id6771163743',
    features: [
      { en: 'End-to-End Production Tracking', ar: 'تتبع شامل لمراحل الغزل والنسيج والصباغة' },
      { en: 'Raw Material & Yarn Inventory', ar: 'إدارة دقيقة لمخزون الخيوط والأقمشة والمستلزمات' },
      { en: 'Garment Costing & Bill of Materials', ar: 'حساب دقيق لتكاليف التشغيل وقوائم المواد (BOM)' },
      { en: 'Mobile App for Floor Supervisors', ar: 'تطبيق موبايل لمشرفي المصانع ومدراء الورديات' },
    ],
    keywords: ['texio', 'textile erp egypt', 'garment factory management', 'textile manufacturing system'],
    relatedBlogCategories: ['Development', 'Automation'],
  },
];

/**
 * Smart device redirect: resolves the ideal URL depending on client environment
 */
export function resolveSmartProjectUrl(project: FlagshipProject): string {
  if (typeof window === 'undefined') {
    return project.webUrl || project.androidUrl || project.iosUrl || '#';
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as unknown as { opera?: string }).opera || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as unknown as { MSStream?: boolean }).MSStream;
  const isAndroid = /android/i.test(userAgent);

  if (isIOS && project.iosUrl) {
    return project.iosUrl;
  }
  if (isAndroid && project.androidUrl) {
    return project.androidUrl;
  }
  return project.webUrl || project.androidUrl || project.iosUrl || '#';
}

/**
 * In-Blog cross-routing: finds matching flagship project based on article category
 */
export function getRecommendedProjectForCategory(category: string): FlagshipProject {
  const matched = flagshipProjects.find((p) =>
    p.relatedBlogCategories.some((c) => c.toLowerCase() === category.toLowerCase())
  );
  return matched || flagshipProjects[0]; // fallback to WhatsApp AI
}
